import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';

import { cn } from '@/core/string.core';

type TooltipSide = 'top' | 'right' | 'bottom' | 'left';
type TooltipAlign = 'start' | 'center' | 'end';

interface TooltipPosition {
  side?: TooltipSide;
  align?: TooltipAlign;
  sideOffset?: number;
  alignOffset?: number;
}

interface TooltipProps {
  /** The content to display inside the tooltip */
  content: React.ReactNode;
  /** The trigger element that will show the tooltip on hover */
  children: React.ReactElement;
  /** Position configuration for the tooltip */
  position?: TooltipPosition;
  /** Delay in ms before showing the tooltip (default: 0) */
  delayDuration?: number;
  /** Hide the arrow pointing to the trigger (default: false) */
  hideArrow?: boolean;
  /** Additional class name for the tooltip content */
  className?: string;
}

const defaultPosition: Required<TooltipPosition> = {
  side: 'top',
  align: 'center',
  sideOffset: 4,
  alignOffset: 0,
};

export const Tooltip = ({
  content,
  children,
  position,
  delayDuration = 0,
  hideArrow = false,
  className,
}: TooltipProps) => {
  const { side, align, sideOffset, alignOffset } = { ...defaultPosition, ...position };

  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration} disableHoverableContent={false}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            align={align}
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            className={cn(
              'z-50 overflow-hidden rounded shadow-md p-3 py-1',
              'bg-white',
              'animate-in fade-in-0 zoom-in-95',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
              'data-[side=bottom]:slide-in-from-top-2',
              'data-[side=left]:slide-in-from-right-2',
              'data-[side=right]:slide-in-from-left-2',
              'data-[side=top]:slide-in-from-bottom-2',
              className,
            )}
          >
            {content}
            {!hideArrow && <TooltipPrimitive.Arrow width={11} height={6} className="drop-shadow-md fill-white" />}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};
